import * as XLSX from 'xlsx';
import type { ParsedFile } from './types';

/**
 * Parse CSV file
 */
const parseCSV = (text: string): ParsedFile => {
  const lines = text.split('\n').filter((line) => line.trim());
  if (lines.length === 0) return { headers: [], rows: [], sampleRows: [] };

  const headers = lines[0].split(',').map((h) => h.trim());
  const rows = lines.slice(1).map((line) => {
    const values = line.split(',').map((v) => v.trim());
    const row: Record<string, any> = {};
    headers.forEach((header, i) => {
      row[header] = values[i] || '';
    });
    return row;
  });

  return {
    headers,
    rows,
    sampleRows: rows.slice(0, 10),
  };
};

/**
 * Parse Excel file (.xlsx, .xls)
 */
const parseXlsx = async (file: File): Promise<ParsedFile> => {
  const data = await file.arrayBuffer();
  const workbook = XLSX.read(data, { type: 'array' });
  const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
  const jsonData = XLSX.utils.sheet_to_json(firstSheet, { header: 1 }) as any[][];

  if (jsonData.length === 0) return { headers: [], rows: [], sampleRows: [] };

  const headers = jsonData[0].map((h) => String(h || '').trim());
  const rows = jsonData.slice(1).map((rowArray) => {
    const row: Record<string, any> = {};
    headers.forEach((header, i) => {
      row[header] = rowArray[i] !== undefined ? String(rowArray[i]) : '';
    });
    return row;
  });

  return {
    headers,
    rows,
    sampleRows: rows.slice(0, 10),
  };
};

/**
 * Composable for parsing various file types
 */
// eslint-disable-next-line import/prefer-default-export
export const useFileParser = () => {
  /**
   * Parse a file based on its extension
   * @param file - The file to parse
   * @returns Parsed file data with headers and rows
   * @throws Error if file type is not supported
   */
  const parseFile = async (file: File): Promise<ParsedFile> => {
    const extension = file.name.split('.').pop()?.toLowerCase();

    if (extension === 'csv' || extension === 'txt') {
      const text = await file.text();
      return parseCSV(text);
    }

    if (extension === 'xlsx' || extension === 'xls') {
      return parseXlsx(file);
    }

    throw new Error(`Unsupported file type: ${extension}`);
  };

  /**
   * Get supported file extensions
   */
  const getSupportedExtensions = (): string[] => ['csv', 'txt', 'xlsx', 'xls'];

  /**
   * Check if a file type is supported
   */
  const isFileTypeSupported = (filename: string): boolean => {
    const extension = filename.split('.').pop()?.toLowerCase();
    return extension ? getSupportedExtensions().includes(extension) : false;
  };

  return {
    parseFile,
    getSupportedExtensions,
    isFileTypeSupported,
  };
};
